import { Clock, MessageSquare, Pencil, Target } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import {
  ENGLISH_ABILITY_DESCRIPTIONS,
  INDUSTRY_LABELS,
  PRIMARY_GOAL_LABELS,
  TEST_TYPE_LABELS,
} from '@/types/learner-portal'
import type {
  IndustryInterest,
  NeedsAnalysisResponse,
  PrimaryGoal,
  SelfAssessedLevel,
  TestType,
} from '@/types/learner-portal'

type Props = {
  readonly needsAnalysis: NeedsAnalysisResponse
  readonly onUpdate?: () => void
}

function goalLabel(goal: string): string {
  return PRIMARY_GOAL_LABELS[goal as PrimaryGoal] ?? goal
}

function specLabel(spec: string): string {
  if (spec in TEST_TYPE_LABELS) return TEST_TYPE_LABELS[spec as TestType]
  if (spec in INDUSTRY_LABELS) return INDUSTRY_LABELS[spec as IndustryInterest]
  return spec
}

function industryLabel(industry: string): string {
  return INDUSTRY_LABELS[industry as IndustryInterest] ?? industry
}

export function NeedsAnalysisSection({ needsAnalysis, onUpdate }: Props) {
  const { t } = useTranslation()
  const level = needsAnalysis.self_assessed_english_level
  const levelDescription = ENGLISH_ABILITY_DESCRIPTIONS[level as SelfAssessedLevel]
  const hasIndustries = needsAnalysis.industry_interests && needsAnalysis.industry_interests.length > 0
  const channels = needsAnalysis.communication_channels ?? []
  const frequency = needsAnalysis.communication_frequency
  const hasCommunicationPreferences = channels.length > 0 || !!frequency

  return (
    <div className='space-y-6'>
      <div>
        <h2 className='mb-2 text-2xl font-bold text-gray-900'>{t('accountDialog.goalsTab', 'Goals')}</h2>
        <p className='text-sm text-gray-600'>
          {t('accountDialog.needsAnalysisDescription', 'Your learning preferences and goals.')}
        </p>
      </div>

      {/* Level */}
      {levelDescription && (
        <div className='rounded-lg border border-gray-300 p-4'>
          <p className='mb-2 text-xs font-medium text-muted-foreground uppercase tracking-wide'>
            {t('accountDialog.inEnglishICan', 'In English, I can…')}
          </p>
          <p className='text-sm text-gray-700'>{levelDescription}</p>
        </div>
      )}

      {/* Goals */}
      <div className='rounded-lg border border-gray-300 p-4'>
        <div className='mb-3 flex items-center gap-2'>
          <Target className='h-4 w-4 text-muted-foreground' />
          <p className='text-xs font-medium text-muted-foreground uppercase tracking-wide'>
            {t('accountDialog.yourGoals', 'Your Goals')}
          </p>
        </div>
        <div className='space-y-1'>
          {needsAnalysis.primary_goals.map((goal) => (
            <p key={goal} className='text-sm font-semibold text-gray-900'>
              {goalLabel(goal)}
            </p>
          ))}
          {needsAnalysis.goal_specification && (
            <p className='text-sm text-gray-700'>{specLabel(needsAnalysis.goal_specification)}</p>
          )}
        </div>
      </div>

      {/* Industries */}
      {hasIndustries && (
        <div className='rounded-lg border border-gray-300 p-4'>
          <p className='mb-3 text-xs font-medium text-muted-foreground uppercase tracking-wide'>
            {t('accountDialog.industryInterests', 'Industry Interests')}
          </p>
          <div className='space-y-1'>
            {needsAnalysis.industry_interests!.map((industry) => (
              <p key={industry} className='text-sm text-gray-700'>
                {industryLabel(industry)}
              </p>
            ))}
          </div>
        </div>
      )}

      {/* Communication preferences (ENG-3474) */}
      {hasCommunicationPreferences && (
        <div className='rounded-lg border border-gray-300 p-4'>
          <div className='mb-3 flex items-center gap-2'>
            <MessageSquare className='h-4 w-4 text-muted-foreground' />
            <p className='text-xs font-medium text-muted-foreground uppercase tracking-wide'>
              {t('accountDialog.communicationPreferences', 'Communication Preferences')}
            </p>
          </div>
          <div className='space-y-1'>
            {channels.map((channel) => (
              <p key={channel} className='text-sm font-semibold text-gray-900'>
                {t(`needsAnalysis.communicationChannels.options.${channel}`, channel)}
              </p>
            ))}
            {frequency && (
              <p className='text-sm text-gray-700'>
                {t(`needsAnalysis.communicationFrequency.options.${frequency}`, frequency)}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Weekly commitment */}
      <div className='flex items-center gap-2 text-sm text-muted-foreground'>
        <Clock className='h-4 w-4' />
        <span>
          {needsAnalysis.weekly_time_commitment} {t('accountDialog.perWeek', '/ week')}
        </span>
      </div>

      {/* Update button */}
      {onUpdate && (
        <Button variant='outline' size='sm' onClick={onUpdate} className='gap-2'>
          <Pencil className='h-3.5 w-3.5' />
          {t('accountDialog.updateResponses', 'Update my responses')}
        </Button>
      )}
    </div>
  )
}
